import React, { useState, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import BaseResponsiveImage from '../BaseResponsiveImage/BaseResponsiveImage';

export interface ResponsiveImageProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'alt'
> {
  alt: string;
  containerSx?: SxProps<Theme>;
  draggable?: boolean | 'true' | 'false';
  /** Shown if the primary src fails to load */
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  sizes?: string;
  src: string;
  srcSet?: string;
  sx?: SxProps<Theme>;
}

/**
 * ResponsiveImage
 *
 * Wraps BaseResponsiveImage with:
 * - Loading skeleton while the image is fetching
 * - Graceful fallback when the primary src fails (tries `fallbackSrc`,
 *   then shows an "Image unavailable" placeholder)
 * - Context-menu / drag protection inherited from BaseResponsiveImage
 *
 * @example
 * <ResponsiveImage src={user.avatar} alt="Profile photo" fallbackSrc="/default-avatar.png" />
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = React.memo(
  ({
    src,
    fallbackSrc,
    alt,
    srcSet,
    sizes,
    loading = 'lazy',
    sx = {},
    containerSx,
    onClick,
    draggable = false,
    ...restProps
  }) => {
    const [currentSrc, setCurrentSrc] = useState(src);
    const [hasError, setHasError] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    // Reset state when src changes
    useEffect(() => {
      setCurrentSrc(src);
      setHasError(false);
      setHasLoaded(false);
    }, [src]);

    const handleError = () => {
      if (fallbackSrc && currentSrc !== fallbackSrc) {
        // First attempt: try the fallback src
        setCurrentSrc(fallbackSrc);
      } else {
        // Fallback also failed — show the placeholder
        setHasError(true);
      }
    };

    const handleLoad = () => {
      setHasLoaded(true);
    };

    if (hasError) {
      return (
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'action.hover',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            width: '100%',
            ...containerSx,
          }}
        >
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontSize: '0.875rem', p: 2, textAlign: 'center' }}
          >
            Image unavailable
          </Box>
        </Box>
      );
    }

    return (
      <>
        {!hasLoaded && (
          <Box
            sx={{
              height: '100%',
              position: 'relative',
              width: '100%',
              ...containerSx,
            }}
          >
            <Skeleton
              animation="wave"
              variant="rectangular"
              sx={{
                bgcolor: 'action.hover',
                height: '100%',
                left: 0,
                position: 'absolute',
                top: 0,
                width: '100%',
                ...sx,
              }}
            />
          </Box>
        )}
        <BaseResponsiveImage
          {...restProps}
          alt={alt}
          containerSx={{
            ...containerSx,
            ...(hasLoaded ? {} : { display: 'none' }),
          }}
          draggable={draggable}
          loading={loading}
          onClick={onClick}
          onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
          onError={handleError}
          onLoad={handleLoad}
          sizes={sizes}
          src={currentSrc}
          srcSet={srcSet}
          sx={sx}
        />
      </>
    );
  },
);

ResponsiveImage.displayName = 'ResponsiveImage';
export default ResponsiveImage;
